/** @jsx jsx */

import {
  Fragment as _,
  useState,
  useContext,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react';
import { css, jsx } from '@emotion/core';
import { interactionContext } from './interactionContext';
import { unlockInteraction } from 'services/unlockInteraction';
import isIE from 'services/isIE';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import getStore from 'services/getStore';
import EditWrapper from './EditWrapper';
import { useSelector } from 'react-redux';
import hotspotDefaultSVG from 'assets/Widest Image.png';
import HotspotSVGError from 'assets/HotspotSVGError.svg';
import hexToRGB from 'services/hexToRGB';
import getBlockById from 'services/apiHelpers/getBlockById';
import Feedback from './Feedback';
import VisibleHotSpot from './VisibleHotSpot';
import { Polygon } from './Polygon';
import Rectangle from './Rectangle';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import pubSub from 'services/pubSub';
import setNewBlockNewTree from 'services/apiHelpers/setNewBlockNewTree';
import { getNextHotSpotBlock } from 'factories/interactions/hotspot_isolated';
import { setBlocksAction } from 'actions/setBlocksAction';
import { getSVGSize } from 'services/getSVGSize';
import { useHotSpotTransformOldToNew } from './useHotSpotTransformOldToNew';
import { colors } from 'styles/variables';
import {
  HotSpotScorableContext as _HotSpotScorableContext,
  IHotSpotScorableContext,
} from './HotSpotScorable';
import { getMouseEvents } from './getMouseEvents';
import { getInteractionsLockedData } from 'services/scorm/scormService';

const store = getStore();
const { lPink } = colors;
const hasBeenSeen: { [key: string]: boolean } = {};

const HotSpot = ({ children, ...props }) => {
  let {
    id,
    image = hotspotDefaultSVG,
    src,
    imgWidth = 960,
    imgHeight = 465,
    hotspotType = 'circle',
    hotSpotColor,
    hotSpotSize,
    hotSpots,
    hotSpotsAnimated = true,
    hotSpotsVisible = true,
    useMediaInFeedback = true,
    showSeenState = true,
    rectangleBorderRadius = 0,
    rectangleDisplayOutlineOnly = false,
    description = '',
  } = props;

  if (src) {
    image = src;
  }

  const context: any = useContext(interactionContext);
  const { immediateFeedback = true, score = false } = context?.props;

  const HotSpotScorableContext: any = useContext(_HotSpotScorableContext);

  const {
    updateAttempt,
    correctAnswers = [],
    selected = [],
    updateSelected,
    submitState,
    showCorrectAnswer,
  } = HotSpotScorableContext;

  const isFocused = getStore().getState().focusedNodeId === id;

  const [imgSize, setImgSize] = useState([imgWidth, imgHeight]);
  const [imgLoading, setImgLoading] = useState(0);
  const [creating, setCreating] = useState(false);

  const [hotSpotFocus, setHotSpotFocus] = useState<string | undefined>(
    undefined
  );

  const [SVGHasNoDimensionsError, setSVGHasNoDimensionsError] = useState<
    boolean
  >(false);

  const [showFeedback, setShowFeedback] = useState<string | null>(null);

  const [{ scale, inverseScale }, setScale] = useState({
    scale: 1 || 0.3,
    inverseScale: 1 || 1 * (1 / 0.3),
  });

  const [currentHotspotId, setCurrentHotspotId] = useState<string | undefined>(
    undefined
  );

  const container = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  const currentHotspot = useRef<string | null>(null);
  const hasPointsOnProps = useRef<boolean | undefined>(undefined);

  const drawing = useRef<boolean>(false);
  const startXY = useRef<{ startX: number; startY: number }>({
    startX: 0,
    startY: 0,
  });
  const rectState = useRef<any>({});

  let { isOpen } = context;

  const interactionsLocked = useSelector(
    (state: any) => state.interactionsLocked
  );

  const interactionsOpen: any = useSelector(
    (state: any) => state.interactionsOpen
  );

  if (interactionsOpen.includes(context.id)) {
    isOpen = true;
  }

  const { r, g, b } = hexToRGB(hotSpotColor);

  const getParsedColor = opacity => {
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  //

  const names: string[] = [];
  const points: (number[] | number)[] = children.map((child, index) => {
    const { name, points, updated } = child?.props;

    names.push(name);

    // if there's no hotpsots it's because they were removed explicitly by code
    // in the last update cycle
    // WE DO NOT WANT TO DO THAT GOING FORWARD !!!
    // we set an updated prop to some value explicitly when that point has been
    // updated in the editor. we can preference it then for use
    // otherwise. we use what's on the hotSpots

    //

    // can't use hotSpots[name] since there's potentially dupes
    return !hotSpots || updated ? points : Object.values(hotSpots)[index];
  });

  const hotSpotFocusRef = useRef<string | undefined>(undefined);
  const rectangleCoords = useRef<any>({});
  const itemsSeen = useRef(unlockInteraction(context.id, names.length));

  //
  useHotSpotTransformOldToNew({
    id,
    hotSpots,
    children,
    getNextHotSpotBlock,
    getBlockById,
    setNewBlockNewTree,
    store,
    setBlocksAction,
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    if (!interactionsLocked.includes(context.id)) {
      return;
    }

    showFeedback && itemsSeen.current(showFeedback);
  });

  useEffect(() => {
    const pS = pubSub.subscribe(`CURRENT_HOTSPOT_ID_${id}`, $ => {
      const { hotspotsBlockId, currentHotspotId } = $;

      setCurrentHotspotId(currentHotspotId);
    });

    return () => {
      pS();
    };
  });

  useEffect(() => {
    const pS = pubSub.subscribe(`ON_HOTSPOT_HOVER`, ({ hotspotId, hover }) => {
      const hotSpotRoot = document.querySelector(`#hotspots-${id}`);

      if (!hotSpotRoot) return;

      const markers = Array.from(
        hotSpotRoot.querySelectorAll(
          hotspotType === 'rectangle' ? 'rect' : hotspotType
        )
      );

      if (!markers.length) {
        return;
      }

      markers.forEach(
        (marker: any) => (marker.style.fill = getParsedColor(0.5))
      );

      if (!hover) return;

      const marker: any = markers.find(
        (marker: any) => marker.id === `hotspot-${hotspotId}`
      );

      if (marker) {
        marker.style.fill = getParsedColor(0.8); // 'rgba(255, 0, 99, 0.8)'
      }
    });

    return () => {
      pS();
    };
  }, [hotspotType, hotSpotColor]);

  useLayoutEffect(() => {
    const onResize = () => {
      if (container.current) {
        const { width, height } = container.current.getBoundingClientRect();
        if (imgSize[0]) {
          let $ = width / imgSize[0];

          if (imgSize[0] <= width) {
            $ = 1;
          }

          if (mounted.current) {
            setScale({
              scale: $,
              inverseScale: 1 * (1 / $),
            });
            //
            requestAnimationFrame(() => {
              pubSub.publish('UPDATE_HOTSPOT_SCALE_FACTORS', {
                scale: $,
                inverseScale: 1 * (1 / $),
              });
            });
          }
        }
      }
    };

    onResize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, imgSize]);

  useEffect(() => {
    setImgLoading(1);
    setSVGHasNoDimensionsError(false);

    //

    let svgImgWidth = imgWidth;
    let svgImgHeight = imgHeight;

    if (/\.(svg)$/i.test(image)) {
      getSVGSize(image)
        .then((size: any) => {
          svgImgWidth = size.width;
          svgImgHeight = size.height;
        })
        .catch(err => {
          if (window.dev()) {
            alert(
              `This svg image does not have a value for width and/or height. It will default to ${svgImgWidth}*${svgImgHeight}`
            );
          }
        })
        .finally(() => {
          setImgLoading(2);
          setImgSize([svgImgWidth, svgImgHeight]);
        });
    } else {
      const img = new Image();
      img.src = image;

      img.onload = () => {
        setImgLoading(2);
        setImgSize([img.width, img.height]);
      };

      // this breaks scaling
      // setImgLoading(2);
      // setImgSize([imgWidth, imgHeight]);
    }
  }, [isOpen, image]);

  let feedback = children.filter(child => {
    const { id, name } = child?.props as any;
    return [id, name].includes(showFeedback);
  });

  feedback = feedback[0]?.props.children;

  const showChecked = (index, id) => {
    if (immediateFeedback || submitState !== 'resolved' || !showCorrectAnswer) {
      return showSeenState && hasBeenSeen[id];
    }

    if (showCorrectAnswer !== undefined) {
      return submitState === 'resolved'
        ? correctAnswers.includes(index)
        : undefined;
    }
  };

  const { onMouseDown, onMouseMove, onMouseUp, onClick } = useMemo(() => {
    return getMouseEvents({
      hotspotType,
      drawing,
      startXY,
      root,
      inverseScale,
      rectState,
      pubSub,
    });
  }, [hotspotType, inverseScale]);

  // gotta fix my deps

  // console.log({ submitState });

  const getShapeProps = ({ id, index }) => {
    return {
      id: `hotspot-${id}`,
      color: getParsedColor(0.8),
      hotSpotsAnimated: submitState === 'resolved' ? false : hotSpotsAnimated,
      hotSpotsVisible,
      size: hotSpotSize * inverseScale,
      inverseScale,
      creating,
      hasBeenSeen: hasBeenSeen[id],
      showSeenState,
      showCorrectAnswer:
        submitState === 'resolved' && showCorrectAnswer
          ? correctAnswers.includes(index)
          : undefined,
      submitState,
      showChecked: showChecked(index, id),
      hotSpotFocus,
      immediateFeedback,
      // score,
      isCorrect: correctAnswers.includes(index),
      enabled: !immediateFeedback && !selected.includes(id),
      onClick(e) {
        if (!immediateFeedback) {
          if (window.dev()) {
            return;
          } else {
            const updatedSelected = updateSelected(index);
            hasBeenSeen[id] = updatedSelected.includes(index);
            return;
          }
        }

        setShowFeedback(hotSpots ? names[index] : id);

        requestAnimationFrame(() => {
          hasBeenSeen[id] = true;
        });
      },
    };
  };

  return !!imgSize.length ? (
    <div
      ref={container}
      css={css`
        position: relative;
        display: flex;
        justify-content: center;
        min-height: ${imgSize[1] * scale}px;

        ${isIE() &&
          `
          width: 100%;
          overflow: hidden;
    `}

        ${submitState === 'resolved' &&
          `
          * {
pointer-events: none !important;
          }
    `}
      `}
    >
      <div
        css={css`
          position: relative;
          width: ${imgSize[0] * scale}px;
          height: ${imgSize[1] * scale}px;
          * {
            margin: 0;
            padding: 0;
          }
        `}
      >
        <div
          ref={root}
          id={`hotspots-${id}`}
          css={css`
            position: absolute;
            left: 0;
            top: 0;
            width: ${imgSize[0]}px;
            height: ${imgSize[1]}px;
            transform: scale(${scale});
            transform-origin: 0 0;
          `}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onClick={onClick}
        >
          {[0, 2].includes(imgLoading) ? (
            <svg
              viewBox={`0 0 ${imgSize[0]} ${imgSize[1]}`}
              preserveAspectRatio="xMidYMid meet"
              onClick={e => {
                if (e.target !== e.currentTarget) {
                  return;
                }
              }}
            >
              <image
                xlinkHref={image || hotspotDefaultSVG}
                width={imgSize[0]}
                height={imgSize[1]}
                style={{
                  pointerEvents: 'none',
                }}
              />
              <title>{description}</title>
              {(!window.dev() ? true : isFocused) &&
                points
                  .filter((points: any) => {
                    if (SVGHasNoDimensionsError) {
                      return false;
                    }

                    if (['rectangle', 'polygon'].includes(hotspotType)) {
                      return true;
                    }

                    if (points?.length && points?.every(entry => entry > 0)) {
                      return true;
                    }
                  })
                  .map((points: any, i) => {
                    const id = children[i]?.props.id;

                    if (hotspotType === 'rectangle') {
                      return (
                        <Rectangle
                          key={`hotspot-${i}-${JSON.stringify(points)}`}
                          {...{
                            ...getShapeProps({ id, index: i }),
                            points: points || [],
                            // color: getParsedColor(
                            //   rectangleDisplayOutlineOnly ? 1 : 0.4
                            // ),
                            color: hotSpotColor,
                            rectangleBorderRadius,
                            rectangleDisplayOutlineOnly,
                            currentHotspotId,
                          }}
                        />
                      );
                    }

                    if (hotspotType === 'polygon') {
                      return (
                        <Polygon
                          key={`hotspot-${i}-${hotSpotColor}`}
                          {...{
                            ...getShapeProps({ id, index: i }),
                            points: (points || []).join(' '),
                            hotSpotColor,
                            r,
                            g,
                            b,
                          }}
                        />
                      );
                    }

                    if (hotspotType === 'circle') {
                      return (
                        <VisibleHotSpot
                          key={`hotspot-${i}`}
                          {...{
                            ...getShapeProps({ id, index: i }),
                            location: [points[0], points[1]],
                          }}
                        />
                      );
                    }
                  })}
            </svg>
          ) : (
            <LoadingSpinner
              size={46 * inverseScale}
              lineWeight={5 * inverseScale}
            />
          )}
        </div>

        <div
          css={css({
            position: 'absolute',
          })}
        >
          {children.map((child, i) => {
            const { id, name } = child.props;
            return (
              <button
                key={`hotspot-focus-trigger-${i}`}
                id={`hotspot-focus-trigger-${id}`}
                css={css({
                  position: 'absolute',
                  outline: 'none',
                  border: 'none',
                  width: 0,
                  height: 0,
                  overflow: 'hidden',
                })}
                aria-label={name}
                onFocus={() => {
                  setHotSpotFocus((hotSpotFocusRef.current = id));
                }}
                onBlur={() => {
                  setHotSpotFocus(undefined);
                }}
                onClick={() => {
                  setShowFeedback(id);

                  requestAnimationFrame(() => {
                    hasBeenSeen[id] = true;
                  });
                }}
              />
            );
          })}
        </div>
      </div>
      {showFeedback && (
        <Feedback
          useMediaInFeedback={useMediaInFeedback}
          onClose={() => {
            if (hotSpotFocusRef.current) {
              requestAnimationFrame(() => {
                const hotSpotFocusTrigger = document.querySelector(
                  `#hotspot-focus-trigger-${hotSpotFocusRef.current}`
                ) as HTMLButtonElement;
                if (hotSpotFocusTrigger) {
                  hotSpotFocusTrigger.focus();
                }
              });
            }
            //
            setShowFeedback(null);
          }}
        >
          {feedback}
        </Feedback>
      )}

      {SVGHasNoDimensionsError && (
        <img
          src={HotspotSVGError}
          css={css({
            position: 'relative',
            width: '100%',
            cursor: 'pointer',
            borderRadius: '45px',

            boxShadow: `0 0 0 1px transparent`,

            ...(isFocused && {
              pointerEvents: 'none',
              boxShadow: `0 0 0 1px ${lPink}`,
            }),

            ':hover': {
              // transition: 'all 0.3s',
              boxShadow: `0 0 0 1px ${lPink}`,
            },
          })}
          onClick={() => {
            setCurrentBlock({ id });
          }}
        />
      )}

      {window.dev() && (!image || imgLoading === 2) && (
        <EditWrapper
          id={props.id}
          width={imgSize[0] * scale}
          height={imgSize[1] * scale}
        />
      )}
    </div>
  ) : null;
};

export default HotSpot;
